import { Component, Optional, Inject } from '@angular/core';
import { PlatformLocation } from '@angular/common';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { Observable, of } from 'rxjs';
import { toDateOrNull, toNumberOrNull, toStringOrNull } from '@max/incident/shared/data-helpers';
import { BaseComponent } from '@max/shared/base-components/base.component';
import { IIncidentModalComponent } from '@max/incident/i-incident-modal.component';
import { FraudIncidentService } from '../services/fraud-incident.service';
import { FraudModel } from '../models/fraud.model';
import { FraudDocumentModel } from '../models/fraud-document.model';
import { DocumentType } from '../../foundation/models/enums/document-type.enum';
import { AccessControlType } from '../../foundation/models/enums/access-control-type.enum';
import { IncidentDialogContext } from '../shared/incident-dialog-context.model';
import { IncidentService } from '../services/incident.service';

@Component({
    selector: 'fraud-customer-modal',
    templateUrl: './fraud-customer-modal.html'
})
export class FraudCustomerModalComponent extends BaseComponent implements IIncidentModalComponent<FraudModel> {
    public form: UntypedFormGroup;
    public incidentNumberId: number;
    public documentTypes: any[] = new DocumentType().GetActive();
    public jurisdictions: any[] = [];
    public canEditNotes = false;
    private incidentTypeId: number = null;
    private accessControlType: AccessControlType = new AccessControlType();

    constructor(
        public dialog: MatDialogRef<FraudCustomerModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA)
        public data: IncidentDialogContext<FraudModel>,
        private fraudIncidentService: FraudIncidentService,
        public incidentService: IncidentService,
        location: PlatformLocation
    ) {
        super();
        location.onPopState(() => dialog.close(null));
        this.canEditNotes = this.auth.hasACSTransactionClaimEx(this.auth.acsTransactionClaim, this.accessControlType.AllowLawEnfNoteEdit.value) == true && this.data.isReadOnly == false;
    }

    public get documents(): UntypedFormArray {
        return this.form.get('documents') as UntypedFormArray;
    }

    public ngOnInit(): void {
        this.incidentNumberId = toNumberOrNull(this.data.incident.incidentNumberId);
    }

    public initLookups = (lookups: any): Observable<any> => {
        this.jurisdictions = lookups.vehicleJurisdictions || [];

        return of(null);
    };

    public closeModal = (result?: any): void => {
        this.dialog.close(result);
    };

    public populateModel = (form: UntypedFormGroup, model: FraudModel): void => {
        model.investigatorName = toStringOrNull(form.get('investigatorName').value);
        model.investigatorPhoneNumber = toStringOrNull(form.get('investigatorPhoneNumber').value);
        model.fraudDocuments = [];

        for (let d of this.documents.controls) {
            let documentModel = new FraudDocumentModel();
            documentModel.id = toNumberOrNull(d.get('documentId').value);
            documentModel.documentTypeId = toNumberOrNull(d.get('documentTypeId').value);
            documentModel.documentNumber = toStringOrNull(d.get('documentNumber').value);
            documentModel.documentIssueDate = toDateOrNull(d.get('documentIssueDate').value);
            documentModel.deleted = true == d.get('deleted').value;
            model.fraudDocuments.push(documentModel);
        }
    };

    public formInitialized = (form: UntypedFormGroup): void => {
        this.form = form;
        form.addControl('investigatorName', new UntypedFormControl(''));
        form.addControl('investigatorPhoneNumber', new UntypedFormControl(''));
        form.addControl('documents', new UntypedFormArray([]));

        if (null != this.incidentTypeId) {
            this.updateFormValidationRules(form, this.incidentTypeId);
        }
    };

    public updateFormValidationRules = (form: UntypedFormGroup, incidentTypeId: number): void => {
        this.incidentTypeId = incidentTypeId;
    };

    public updateData = (form: UntypedFormGroup, incident: FraudModel): void => {
        form.get('investigatorName').setValue(toStringOrNull(incident.investigatorName));
        form.get('investigatorPhoneNumber').setValue(toStringOrNull(incident.investigatorPhoneNumber));
        let documents = form.get('documents') as UntypedFormArray;
        documents.clear();

        if (null != incident.fraudDocuments) {
            for (let d of incident.fraudDocuments) {
                let group = this.buildDocumentFormElement(d);
                documents.push(group);
            }
        }
    };

    public getIncident = (incidentNumberId: number): Observable<FraudModel> => this.fraudIncidentService.getIncident(incidentNumberId);

    public save = (model: FraudModel): Observable<any> => this.fraudIncidentService.saveIncident(model);

    public loadComplete = (): void => {
    };

    public addDocument = (): void => {
        let group = this.buildDocumentFormElement();
        this.documents.push(group);
    };

    public deleteDocument = (document: UntypedFormGroup): void => {
        document.get('deleted').setValue(true);
    };

    private buildDocumentFormElement = (document?: FraudDocumentModel): UntypedFormGroup => {
        let group = new UntypedFormGroup({
            documentId: new UntypedFormControl(null),
            documentTypeId: new UntypedFormControl(null, Validators.required),
            documentNumber: new UntypedFormControl(''),
            documentIssueDate: new UntypedFormControl(null),
            deleted: new UntypedFormControl(false)
        });

        let documentTypeId = null;

        if (null != document) {
            documentTypeId = toNumberOrNull(document.documentTypeId);

            group.get('documentId').setValue(toNumberOrNull(document.id));
            group.get('documentTypeId').setValue(documentTypeId);
            group.get('documentNumber').setValue(toStringOrNull(document.documentNumber));
            group.get('documentIssueDate').setValue(toDateOrNull(document.documentIssueDate));
        }

        if (null == documentTypeId) {
            group.get('documentNumber').disable();
        }

        group.get('documentTypeId').valueChanges.subscribe(v => {
            if (null == v) {
                group.get('documentNumber').disable();
            } else {
                group.get('documentNumber').enable();
            }
        });

        return group;
    };
}
